import React, { useState } from 'react';
import styled from 'styled-components';
import ConfirmModal from './ConfirmModal';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  closeOnOverlayClick?: boolean;
  showCloseConfirmation?: boolean;
}

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
`;

const ModalContainer = styled.div<{ $size: string }>`
  background: white;
  border-radius: 8px;
  width: ${props => {
    switch (props.$size) {
      case 'sm': return '400px';
      case 'md': return '600px';
      case 'lg': return '800px';
      case 'xl': return '1200px';
      default: return '600px';
    }
  }};
  max-width: 95vw;
  max-height: 95vh;
  overflow-y: auto;
  position: relative;
`;

const Header = styled.div`
  padding: 1rem;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8fafc;
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  color: #1e293b;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 400;
`;

const CloseButton = styled.button`
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 1.2rem;
  color: #64748b;
  cursor: pointer;
  padding: 0.5rem 0.75rem;
  line-height: 1;
  transition: all 0.2s ease;
  
  &:hover {
    background: #e2e8f0;
    color: #1e293b;
    border-color: #cbd5e1;
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const Content = styled.div`
  padding: 1.5rem;
`;

const Modal: React.FC<ModalProps> = ({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  size = 'md',
  closeOnOverlayClick = false,
  showCloseConfirmation = false
}) => {
  const [showConfirm, setShowConfirm] = useState(false);

  if (!isOpen) return null;

  const handleOverlayClick = () => {
    if (closeOnOverlayClick) {
      handleClose();
    }
  };

  const handleClose = () => {
    if (showCloseConfirmation) {
      setShowConfirm(true);
    } else {
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setShowConfirm(false);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleClose();
    }
  };

  return (
    <>
      <Overlay 
        $isOpen={isOpen} 
        onClick={handleOverlayClick}
        onKeyDown={handleKeyDown}
        tabIndex={-1}
      >
        <ModalContainer $size={size} onClick={e => e.stopPropagation()}>
          <Header>
            <TitleContainer>
              <Title>{title}</Title>
              {showCloseConfirmation && (
                <Subtitle>Use o botão × para fechar com segurança</Subtitle>
              )}
            </TitleContainer>
            <CloseButton onClick={handleClose}>&times;</CloseButton>
          </Header>
          <Content>{children}</Content>
        </ModalContainer>
      </Overlay>

      <ConfirmModal
        isOpen={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={handleConfirmClose}
        title="Fechar Formulário"
        message="Tem certeza que deseja fechar? As alterações não salvas serão perdidas."
        confirmText="Sim, Fechar"
        cancelText="Continuar Editando"
        type="warning"
      />
    </>
  );
};

export default Modal; 
